import { useState, useRef } from 'react'
import { useParams } from 'react-router'
import AppLayout from '@cloudscape-design/components/app-layout'
import Page from '@/components/common/Page'
import DirectoryTab from '@/pages/clients/ClientProviders/DirectoryTab'
import ProviderDetailPanel from '@/pages/clients/ClientProviders/ProviderDetailPanel'
import { useEnrichProviderEmail, useEnrichProviderCms } from '@/hooks/useProviders'
import type { Provider } from '@/lib/types'

export default function DirectoryPage() {
  const { clientId } = useParams<{ clientId: string }>()
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null)
  const [splitPanelOpen, setSplitPanelOpen] = useState(false)
  const { enrichEmail, loading: emailLoading } = useEnrichProviderEmail()
  const { enrichCms } = useEnrichProviderCms()
  const pendingForRef = useRef<string | null>(null)

  if (!clientId) return null

  const handleProviderSelect = (provider: Provider | null) => {
    if (provider && selectedProvider?._id === provider._id) {
      setSplitPanelOpen(false)
      return
    }
    setSelectedProvider(provider)
    setSplitPanelOpen(!!provider)
    pendingForRef.current = provider?._id ?? null
    if (provider && !provider.email) {
      const targetId = provider._id
      enrichEmail(clientId, provider._id).then(result => {
        if (pendingForRef.current !== targetId) return
        const enriched = result.data?.enrichProviderEmail
        if (enriched) setSelectedProvider(prev =>
          prev?._id === targetId ? { ...prev, email: enriched.email, email_last_checked: enriched.email_last_checked } : prev
        )
      }).catch(() => {})
    }
    if (provider && !provider.website_url) {
      const targetId = provider._id
      enrichCms(clientId, provider._id).then(result => {
        if (pendingForRef.current !== targetId) return
        const enriched = result.data?.enrichProviderCms
        if (enriched) setSelectedProvider(prev =>
          prev?._id === targetId
            ? { ...enriched, email: prev?.email ?? enriched.email, email_last_checked: prev?.email_last_checked ?? enriched.email_last_checked }
            : prev
        )
      }).catch(() => {})
    }
  }

  return (
    <Page
      title="Directory"
      helpContent={{
        header: 'Providers Directory',
        body: (
          <div>
            <p>Discover and explore business partners and providers relevant to your industry. Build your outreach list strategically.</p>
            <h4>How to Use:</h4>
            <ul style={{ marginLeft: 20 }}>
              <li>Search or filter providers by name, location, or type</li>
              <li>Click on a provider to view details</li>
              <li>View contact info, business type, and rating</li>
              <li>Check interaction history</li>
              <li>Generate personalized outreach emails</li>
            </ul>
            <h4>Tips:</h4>
            <p>Review interaction history to avoid duplicate outreach. Use the detail panel to understand a provider before reaching out.</p>
          </div>
        ),
      }}
    >
      <AppLayout
        navigationHide
        toolsHide
        splitPanelOpen={splitPanelOpen}
        onSplitPanelToggle={({ detail }) => setSplitPanelOpen(detail.open)}
        splitPanel={selectedProvider
          ? <ProviderDetailPanel provider={selectedProvider} emailLoading={emailLoading} />
          : undefined}
        content={
          <DirectoryTab clientId={clientId} onProviderSelect={handleProviderSelect} />
        }
      />
    </Page>
  )
}
